/**
 * FairwayBid – die Merkliste eines Händlers
 *
 * Ein Anbieter liest Anfragen zwischen zwei Terminen. Er sieht eine, die
 * passen könnte, kann sie aber jetzt nicht rechnen – der Kollege muss den
 * Einkaufspreis nennen, die Verfügbarkeit ist unklar, es ist Freitagabend.
 * Bisher blieb ihm nur, den Browser-Tab offen zu lassen oder die Anfrage
 * abends in der Suche wiederzufinden. Beides geht meistens schief, und die
 * Anfrage läuft ab, ohne dass jemand sie vergessen hätte.
 *
 * Also eine Merkliste: ein Klick neben "Angebot einreichen", und die Anfrage
 * steht oben im Menü, bis sie bearbeitet ist.
 *
 * Wo sie liegt
 * ------------
 * In den privaten Profildaten des Händlers (privateData). Drei Gründe:
 *
 *   Sie gehört ihm. Welche Anfragen ein Anbieter im Auge behält, ist
 *   Geschäftsinteresse – das sieht weder die Anlage noch ein Wettbewerber.
 *   privateData ist genau dafür da: Nur der Nutzer selbst liest es.
 *
 *   Sie folgt ihm. Am Telefon gemerkt, am Schreibtisch bearbeitet. Im
 *   Browser-Speicher läge sie auf einem Gerät und wäre beim nächsten
 *   Aufräumen weg.
 *
 *   Sie kostet nichts. Es sind Kennungen, keine Kopien der Anfragen. Was in
 *   der Anfrage steht, wird beim Öffnen frisch geladen – eine gemerkte
 *   Anfrage zeigt nie einen alten Stand.
 *
 * Gespeichert werden nur Kennungen (UUIDs), keine Titel, keine Preise.
 *
 * Was wieder verschwindet
 * -----------------------
 * Eine Merkliste, die alles behält, ist nach drei Wochen eine Halde. Deshalb
 * fällt heraus, was sich erledigt hat: abgelaufene Fristen, zurückgezogene
 * oder geschlossene Anfragen, gelöschte sowieso. Das passiert beim Ansehen
 * der Liste – der Eintrag wird nicht nur ausgeblendet, sondern auch aus dem
 * Profil entfernt (siehe aufgeraeumt). Sonst sammelten sich dort stillschweigend
 * Kennungen, die niemand je wieder braucht.
 */

import { getDeadlineStatus } from './deadlines';

/** Der Schlüssel in den privaten Profildaten. */
export const MERK_KEY = 'merkliste';

/**
 * Obergrenze. Wer hundert Anfragen merkt, merkt sich nichts mehr – und die
 * Liste lädt hundert Anfragen einzeln nach. Die Grenze schützt beides.
 */
export const MAX_MERK = 100;

const privat = user => user?.attributes?.profile?.privateData || {};

/** Die gemerkten Kennungen dieses Nutzers, in der Reihenfolge des Merkens. */
export const holeMerkliste = user => {
  const wert = privat(user)[MERK_KEY];
  return Array.isArray(wert) ? wert.filter(id => typeof id === 'string' && id.length > 0) : [];
};

/** Die Kennung einer Anfrage – egal ob Entity oder blanke UUID. */
export const kennung = listingOderId =>
  typeof listingOderId === 'string' ? listingOderId : listingOderId?.id?.uuid || null;

/** Steht diese Anfrage auf der Merkliste? */
export const istGemerkt = (user, listingOderId) => {
  const id = kennung(listingOderId);
  return !!id && holeMerkliste(user).includes(id);
};

/**
 * Die neue Liste nach einem Klick auf den Merkknopf.
 *
 * Neues steht vorn: Was zuletzt aufgefallen ist, ist meist das, was als
 * Nächstes drankommt.
 */
export const umgeschaltet = (liste = [], id) => {
  if (!id) return liste;
  return liste.includes(id) ? liste.filter(x => x !== id) : [id, ...liste].slice(0, MAX_MERK);
};

/**
 * Der Aufruf für sdk.currentUser.updateProfile.
 *
 * privateData wird von Sharetribe flach zusammengeführt: Andere Schlüssel
 * darin bleiben unberührt, auch wenn sie hier nicht genannt werden.
 */
export const merkAenderung = (user, listingOderId) => {
  const id = kennung(listingOderId);
  return { privateData: { [MERK_KEY]: umgeschaltet(holeMerkliste(user), id) } };
};

/**
 * Ist diese Anfrage noch eine offene Anfrage?
 *
 * Drei Fälle, in denen sie von der Merkliste fällt:
 *
 *   gelöscht    Sharetribe liefert die Anzeige mit deleted: true oder gar
 *               nicht mehr – beides kommt hier als "nein" an.
 *   geschlossen Die Anlage hat sie zurückgezogen oder vergeben. Ein Angebot
 *               ist darauf nicht mehr möglich.
 *   abgelaufen  Die Angebotsfrist ist vorbei.
 *
 * Ein Entwurf oder eine Anzeige in Prüfung kann gar nicht gemerkt worden
 * sein; sie fällt der Vollständigkeit halber trotzdem durch.
 */
export const nochOffen = (listing, now = new Date()) => {
  const a = listing?.attributes;
  if (!a || a.deleted === true) return false;
  if (a.state !== 'published') return false;
  const frist = getDeadlineStatus(listing, now);
  return !frist || !frist.expired;
};

/**
 * Teilt die geladenen Anfragen in "zeigen" und "aus dem Profil nehmen".
 *
 * Kennungen ohne geladene Anfrage gelten als gelöscht: Wer sie nicht mehr
 * laden kann, kann auch kein Angebot mehr darauf schreiben.
 *
 * @param {Array<string>} ids die gemerkten Kennungen, in ihrer Reihenfolge
 * @param {Array<Object>} listings die geladenen Anfragen, in beliebiger Ordnung
 * @returns {{offen: Array, verfallen: Array<string>, bleibt: Array<string>}}
 */
export const aufgeraeumt = (ids = [], listings = [], now = new Date()) => {
  const nachId = new Map(
    (Array.isArray(listings) ? listings : []).filter(l => l?.id?.uuid).map(l => [l.id.uuid, l])
  );

  const offen = [];
  const verfallen = [];

  ids.forEach(id => {
    const listing = nachId.get(id);
    if (listing && nochOffen(listing, now)) offen.push(listing);
    else verfallen.push(id);
  });

  return { offen, verfallen, bleibt: offen.map(l => l.id.uuid) };
};
